/**
 * protein78 Markalar sayfası (sections/cd-brand-directory.liquid).
 * Modül olarak yüklenir (type="module" → ertelenmiş, aynı URL bir kez çalışır).
 *
 * <cd-brand-directory>: arama kutusu marka kartlarını istemci tarafında süzer. Eşleşmeyen kartlar, boş kalan harf
 * grupları ve harf çubuğundaki bağlantıları gizlenir; sonuç tek canlı bölgeden (ref="durum") duyurulur.
 * Enter'a basıldığında tek sonuç kaldıysa o markaya gider. JS yokken arama kutusu gizli kalır.
 */
import { Component } from '@theme/component';

/**
 * Türkçe büyük/küçük harf ve işaretlerden bağımsız arama anahtarı: "Sci-Tech" → "scitech", "IŞIK"/"ışık" → "isik".
 * @param {string} metin
 */
const katla = (metin) =>
  metin
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');

/**
 * @typedef {object} MarkaRefs
 * @property {HTMLElement} [aramaKutusu]
 * @property {HTMLInputElement} [girdi]
 * @property {HTMLElement} [durum]
 * @property {HTMLElement} [bos]
 * @property {HTMLElement[]} [grup]
 * @property {HTMLElement[]} [harf]
 */

/**
 * @extends {Component<MarkaRefs>}
 */
class CdBrandDirectory extends Component {
  /** @type {{ oge: HTMLElement, ad: string }[]} */
  #ogeler = [];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #duyuru;

  /** Son süzmede görünen kart sayısı */
  #gorunen = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#dizinle();
    if (this.refs.aramaKutusu) this.refs.aramaKutusu.hidden = false;
    // Geri gelindiğinde tarayıcı kutudaki metni geri yükleyebilir
    if (this.refs.girdi?.value) this.filtrele();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#dizinle();
    if (this.refs.aramaKutusu) this.refs.aramaKutusu.hidden = false;
    this.filtrele();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#duyuru);
  }

  #dizinle() {
    this.#ogeler = [];
    for (const oge of this.querySelectorAll('[data-marka]')) {
      if (oge instanceof HTMLElement) this.#ogeler.push({ oge, ad: katla(oge.dataset.marka ?? '') });
    }
  }

  /** Arama kutusuna her yazıldığında çalışır (on:input). */
  filtrele() {
    const ham = this.refs.girdi?.value.trim() ?? '';
    const sorgu = katla(ham);
    let gorunen = 0;

    for (const { oge, ad } of this.#ogeler) {
      const eslesti = sorgu === '' || ad.includes(sorgu);
      oge.hidden = !eslesti;
      if (eslesti) gorunen += 1;
    }
    this.#gorunen = gorunen;

    const gruplar = this.refs.grup ?? [];
    const harfler = this.refs.harf ?? [];
    gruplar.forEach((grup, i) => {
      const dolu = grup.querySelector('[data-marka]:not([hidden])') !== null;
      grup.hidden = !dolu;
      const harf = harfler[i];
      if (harf) harf.hidden = !dolu;
    });

    // İşlev olarak verilir: yazılan "$&", "$'" gibi diziler değiştirme kalıbı sayılmasın
    const bosMetin = (this.dataset.bos ?? '').replace('[arama]', () => ham);
    const { bos } = this.refs;
    if (bos) {
      bos.textContent = bosMetin;
      bos.hidden = gorunen > 0 || sorgu === '';
    }

    this.#duyur(sorgu === '' ? '' : gorunen > 0 ? (this.dataset.sonuc ?? '').replace('[sayi]', String(gorunen)) : bosMetin);
  }

  /**
   * Enter: tek sonuç kaldıysa o markanın sayfasına git.
   * @param {KeyboardEvent} event
   */
  tus(event) {
    if (event.key !== 'Enter' || event.isComposing || this.#gorunen !== 1) return;
    const hedef = this.#ogeler.find(({ oge }) => !oge.hidden)?.oge.querySelector('a');
    if (!(hedef instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    hedef.click();
  }

  /**
   * Yazarken her harfte değil, kısa bir duraksamadan sonra duyurur.
   * @param {string} metin
   */
  #duyur(metin) {
    clearTimeout(this.#duyuru);
    const { durum } = this.refs;
    if (!durum) return;
    this.#duyuru = setTimeout(() => {
      durum.textContent = metin;
    }, 500);
  }
}

if (!customElements.get('cd-brand-directory')) customElements.define('cd-brand-directory', CdBrandDirectory);
